import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth";
import { listShiftSummaries, generateShiftSummary, downloadShiftSummary } from "@/lib/shiftReports.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/shift-reports")({ component: ShiftReportsPage });

function ShiftReportsPage() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const qc = useQueryClient();
  const list = useServerFn(listShiftSummaries);
  const gen = useServerFn(generateShiftSummary);
  const dl = useServerFn(downloadShiftSummary);

  const reports = useQuery({
    queryKey: ["shift-reports", orgId],
    queryFn: () => list({ data: { orgId: orgId! } }),
    enabled: !!orgId,
  });

  const [shiftId, setShiftId] = useState("");
  const generate = useMutation({
    mutationFn: () => gen({ data: { shift_id: shiftId } }),
    onSuccess: () => { toast.success("Summary generated"); setShiftId(""); qc.invalidateQueries({ queryKey: ["shift-reports", orgId] }); },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const onDownload = async (id: string) => {
    try {
      const { url } = await dl({ data: { id } });
      window.open(url, "_blank");
    } catch (e: any) { toast.error(e?.message ?? "Download failed"); }
  };

  return (
    <div className="space-y-4 p-4">
      <div>
        <h1 className="text-2xl font-bold">Shift Reports</h1>
        <p className="text-sm text-muted-foreground">Generated end-of-shift PDF summaries.</p>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Generate report</CardTitle></CardHeader>
        <CardContent className="flex gap-2">
          <Input aria-label="Shift ID" placeholder="Shift ID" value={shiftId} onChange={(e) => setShiftId(e.target.value)} />
          <Button onClick={() => generate.mutate()} disabled={!shiftId || generate.isPending}>
            {generate.isPending ? "Generating…" : "Generate"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Recent reports</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(reports.data ?? []).map((r: any) => (
            <div key={r.id} className="flex items-center justify-between rounded-md border border-border p-2 text-sm">
              <div className="min-w-0 flex-1">
                <Link to="/tracking/$shiftId" params={{ shiftId: r.shift_id }} className="truncate font-medium hover:underline">
                  Shift {r.shift_id.slice(0, 8)}
                </Link>
                <div className="text-xs text-muted-foreground">{new Date(r.generated_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={r.status === "ready" ? "secondary" : "outline"}>{r.status}</Badge>
                <Button size="sm" variant="outline" onClick={() => onDownload(r.id)} disabled={!r.pdf_path}>PDF</Button>
              </div>
            </div>
          ))}
          {(reports.data ?? []).length === 0 && <div className="text-sm text-muted-foreground">No reports yet.</div>}
        </CardContent>
      </Card>
    </div>
  );
}
